import { useEffect, useState } from "react";
import { redirectToSignIn } from "../api/auth.ts";
import { getBrandTitle } from "../api/client.ts";
import { ThemeSwitcher } from "../components/ui/themeSwitcher.tsx";
import { Logo } from "../components/ui/logo.tsx";
import { getSignInOverrideIP, setSignInOverrideIP } from "./signInOverride.ts";
import styles from "./signInPage.module.scss";

const SESSION_ENDED_KEY = "wac_session_ended_reason";

function readAndClearSessionEndedReason(): string | null {
  try {
    const v = sessionStorage.getItem(SESSION_ENDED_KEY);
    if (v) sessionStorage.removeItem(SESSION_ENDED_KEY);
    return v;
  } catch {
    return null;
  }
}

function bannerMessageFor(reason: string | null): string | null {
  switch (reason) {
    case "refresh-failed":
      return "Your session is no longer valid (your sign-in may have been revoked). Please sign in again.";
    case "session-expired":
      return "Your session has expired. Please sign in again.";
    default:
      return null;
  }
}

export function SignInPage() {
  const [overrideIP, setOverrideIP] = useState<string>(() => getSignInOverrideIP() ?? "");
  // Consume the session-ended reason once on mount — surviving F5 (sessionStorage)
  // but not a fresh tab, so a user opening the app cleanly never sees a stale banner.
  const [endedReason] = useState<string | null>(() => readAndClearSessionEndedReason());
  const bannerMessage = bannerMessageFor(endedReason);

  // Defensive: if React ever remounts SignInPage after a route change, the stash
  // has already been consumed by the first mount, so subsequent renders just see
  // no banner. Keep the cleanup here as a no-op in normal flow.
  useEffect(() => () => { /* nothing — sessionStorage already cleared on read */ }, []);

  // Debug helper: write the reason into sessionStorage and reload, so the banner
  // renders via the same path the real code takes. Reloading also exercises the
  // read-and-clear pattern. DEV-only, gated by __DEV_FEATURES__ below.
  const simulateSessionEnded = (reason: "refresh-failed" | "session-expired") => {
    try { sessionStorage.setItem(SESSION_ENDED_KEY, reason); } catch { /* ignore */ }
    window.location.reload();
  };

  return (
    <div className={styles.signInPanel}>
      <div className={styles.themeSwitcherCorner}>
        <ThemeSwitcher />
      </div>
      <div className={styles.brandBlock}>
        <Logo className={styles.brandLogo} />
        <span className={styles.brandText}>{getBrandTitle()}</span>
      </div>
      <div className={styles.signInCard}>
        {bannerMessage && (
          <div className={styles.signInEndedBanner} role="alert">
            {bannerMessage}
          </div>
        )}
        {__DEV_FEATURES__ && (
          <details className={styles.debugBlock} open={overrideIP !== ""}>
            <summary className={styles.debugSummary}>debug</summary>
            <div className={styles.debugBody}>
              <input
                className={styles.debugInput}
                type="text"
                placeholder="Sign-in IP override (e.g. 192.168.1.42)"
                value={overrideIP}
                onChange={(e) => {
                  setOverrideIP(e.target.value);
                  setSignInOverrideIP(e.target.value);
                }}
              />
              <div className={styles.debugRow}>
                <span className={styles.debugLabel}>Simulate session-ended banner:</span>
                <button
                  type="button"
                  className={styles.debugMiniBtn}
                  onClick={() => simulateSessionEnded("refresh-failed")}
                >
                  refresh-failed
                </button>
                <button
                  type="button"
                  className={styles.debugMiniBtn}
                  onClick={() => simulateSessionEnded("session-expired")}
                >
                  session-expired
                </button>
              </div>
            </div>
          </details>
        )}
        <button
          onClick={() => { void redirectToSignIn(); }}
          className={styles.signInBtn}
        >
          Sign in
        </button>
      </div>
    </div>
  );
}
